<template>
  <main class="go-page" :class="appStore.theme">
    <header>
      <p>{{ t('go.round_progress', { current: round, total: totalRounds }) }}</p>
      <h1>{{ localized(game.name) || 'GO!' }}</h1>
    </header>

    <section class="players" :style="{ '--score-columns': Math.min(4, Math.max(2, players.length)) }">
      <article v-for="player in players" :key="player.id" class="player" :style="{ '--player-colour': player.playerColour }">
        <span class="avatar">{{ initials(playerName(player)) }}</span>
        <span class="player-info">
          <strong>{{ playerName(player) }}</strong>
          <small class="player-status" :class="{ error: falseStarts.has(player.id), submitted: respondedThisRound.has(player.id) }">
            {{ playerRoundStatus(player) }}
          </small>
        </span>
        <b class="score">{{ scores[player.id] || 0 }}</b>
        <i class="connection-dot" :class="{ offline: player.connected === false, away: player.connected !== false && !player.inGame }" :style="{ backgroundColor: player.connected === false ? '#ff4d5e' : player.inGame === true ? '#40dc74' : '#ffad32' }"></i>
      </article>
    </section>

    <section class="signal" :class="phase">
      <div class="signal-light">{{ phase === 'go' ? t('go.go') : phase === 'round-result' ? '✓' : '…' }}</div>
      <h2>{{ message }}</h2>
      <p v-if="phase === 'go'">{{ t('go.tap_now') }}</p>
    </section>

    <section v-if="matchFinished" class="result-overlay">
      <div class="fireworks"><i v-for="spark in 48" :key="spark" :style="confettiStyle(spark)"></i></div>
      <div class="result-card">
        <div class="trophy">🏆</div>
        <h2>{{ finalTitle }}</h2>
        <div class="result-head"><span>{{ t('go.player') }}</span><span>{{ t('go.score') }}</span><span>{{ t('go.average') }}</span><span>{{ t('go.best') }}</span></div>
        <ol><li v-for="player in rankedPlayers" :key="player.id"><span>{{ playerName(player) }}</span><b>{{ scores[player.id] || 0 }}</b><span>{{ averageReaction(player.id) }}</span><span>{{ bestReaction(player.id) }}</span></li></ol>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/appStore'
import { useWebsocketStore } from '@/stores/websocketStore'
import eventBus from '@/plugins/eventBus'
import { soundManager, SOUNDS } from '@/services/soundManager'

const appStore = useAppStore()
const socketStore = useWebsocketStore()
const { t, locale } = useI18n()
const phase = ref('waiting')
const winnerId = ref('')
const reactionTime = ref(0)
const falseStarts = ref(new Set())
const respondedThisRound = ref(new Set())
const round = ref(1)
const totalRounds = 10
const scores = reactive({})
const reactions = reactive({})
const roundReactions = reactive({})
const matchFinished = ref(false)
const acceptedRequestIds = new Set()
let signalTimer
let signalAt = 0

const game = computed(() => appStore.room?.game || appStore.game || {})
const players = computed(() => game.value.players || [])
const winner = computed(() => players.value.find((player) => player.id === winnerId.value))
const rankedPlayers = computed(() => [...players.value].sort((left, right) => (scores[right.id] || 0) - (scores[left.id] || 0)))
const finalWinners = computed(() => {
  const topScore = Math.max(0, ...players.value.map((player) => scores[player.id] || 0))
  return players.value.filter((player) => (scores[player.id] || 0) === topScore)
})
const finalTitle = computed(() => finalWinners.value.length === 1
  ? t('go.player_wins', { player: playerName(finalWinners.value[0]) })
  : t('go.players_tie', { players: finalWinners.value.map(playerName).join(', ') }))
const eligiblePlayers = computed(() => players.value.filter((player) => !falseStarts.value.has(player.id)))
const message = computed(() => {
  if (phase.value === 'waiting') return t('go.wait_for_signal')
  if (phase.value === 'go') return t('go.signal_shown')
  return winner.value ? t('go.round_winner', { player: playerName(winner.value), milliseconds: reactionTime.value }) : ''
})

function localized(value) { return typeof value === 'object' ? value[locale.value] || value.en || Object.values(value)[0] : value }
function playerName(player) { return player?.displayName || player?.name || player?.email || t('go.player') }
function initials(name) { return String(name).trim().split(/\s+/).map((word) => word[0]).join('').slice(0, 2).toUpperCase() }
function playerRoundStatus(player) {
  if (falseStarts.value.has(player.id)) return t('go.false_start')
  if (Object.prototype.hasOwnProperty.call(roundReactions, player.id)) return `${roundReactions[player.id]} ms`
  if (phase.value === 'round-result') return '—'
  return t('go.preparing')
}

function logControl(control, outcome, details = {}) {
  console.info('[GO control]', {
    outcome,
    round: round.value,
    phase: phase.value,
    userId: control?.userId || null,
    requestId: control?.requestId || null,
    clientSentAt: control?.clientSentAt || null,
    receiverReceivedAt: Date.now(),
    ...details
  })
}

function acknowledgeControl(control, accepted, reason) {
  if (!control?.requestId || !control?.userId) return
  socketStore.send({
    event: 'control-result',
    data: { requestId: control.requestId, userId: control.userId, accepted, reason }
  })
}

function beginRound() {
  soundManager.stopAll(); soundManager.play(SOUNDS.roundStart)
  clearTimeout(signalTimer)
  phase.value = 'waiting'
  winnerId.value = ''
  reactionTime.value = 0
  signalAt = 0
  falseStarts.value = new Set()
  respondedThisRound.value = new Set()
  Object.keys(roundReactions).forEach((key) => delete roundReactions[key])
  signalTimer = setTimeout(() => {
    signalAt = performance.now()
    phase.value = 'go'
    console.info('[GO signal]', { round: round.value, shownAt: Date.now(), eligiblePlayers: eligiblePlayers.value.map((player) => player.id) })
    soundManager.play(SOUNDS.goSignal)
  }, 2000 + Math.random() * 4000)
}

function resetMatch() {
  for (const key of Object.keys(scores)) delete scores[key]
  for (const key of Object.keys(reactions)) delete reactions[key]
  players.value.forEach((player) => { scores[player.id] = 0; reactions[player.id] = [] })
  acceptedRequestIds.clear()
  round.value = 1
  matchFinished.value = false
  beginRound()
}

function averageReaction(playerId) {
  const times = reactions[playerId] || []
  if (!times.length) return '—'
  return `${Math.round(times.reduce((sum, time) => sum + time, 0) / times.length)} ms`
}
function bestReaction(playerId) {
  const times = reactions[playerId] || []
  return times.length ? `${Math.min(...times)} ms` : '—'
}

function handleControl(control) {
  if (control?.type !== 'action' || control?.value !== 'tap') {
    logControl(control, 'rejected', { reason: 'invalid-control' })
    acknowledgeControl(control, false, 'invalid-control')
    return
  }
  if (control?.requestId && acceptedRequestIds.has(control.requestId)) {
    logControl(control, 'duplicate', { reason: 'request-already-processed' })
    acknowledgeControl(control, true, 'duplicate')
    return
  }
  if (phase.value === 'finished') {
    logControl(control, 'rejected', { reason: 'match-finished' })
    acknowledgeControl(control, false, 'match-finished')
    return
  }
  const player = players.value.find((candidate) => candidate.id === control.userId)
  if (!player) {
    logControl(control, 'rejected', { reason: 'unknown-player' })
    acknowledgeControl(control, false, 'unknown-player')
    return
  }
  if (falseStarts.value.has(player.id) || respondedThisRound.value.has(player.id)) {
    if (control.requestId) acceptedRequestIds.add(control.requestId)
    logControl(control, 'duplicate', { reason: 'player-already-recorded' })
    acknowledgeControl(control, true, 'player-already-recorded')
    return
  }

  if (phase.value === 'waiting') {
    if (control.requestId) acceptedRequestIds.add(control.requestId)
    soundManager.play(SOUNDS.goFalseStart)
    falseStarts.value = new Set([...falseStarts.value, player.id])
    logControl(control, 'accepted', { reason: 'false-start' })
    acknowledgeControl(control, true, 'false-start')
    if (eligiblePlayers.value.length === 0) {
      clearTimeout(signalTimer)
      signalTimer = setTimeout(beginRound, 1800)
    }
    return
  }

  if (!['go', 'round-result'].includes(phase.value)) {
    logControl(control, 'rejected', { reason: 'invalid-phase' })
    acknowledgeControl(control, false, 'invalid-phase')
    return
  }

  const playerReaction = Math.max(0, Math.round(performance.now() - signalAt))
  if (control.requestId) acceptedRequestIds.add(control.requestId)
  respondedThisRound.value = new Set([...respondedThisRound.value, player.id])
  roundReactions[player.id] = playerReaction
  reactions[player.id] ||= []
  reactions[player.id].push(playerReaction)
  const isRoundWinner = phase.value !== 'round-result'
  logControl(control, 'accepted', { reason: isRoundWinner ? 'round-winner' : 'reaction-recorded', reactionMs: playerReaction })
  acknowledgeControl(control, true, isRoundWinner ? 'round-winner' : 'reaction-recorded')
  if (!isRoundWinner) return

  reactionTime.value = playerReaction
  winnerId.value = player.id
  scores[player.id] = (scores[player.id] || 0) + 1
  soundManager.play(SOUNDS.points)
  phase.value = 'round-result'
  if (round.value >= totalRounds) {
    signalTimer = setTimeout(finishMatch, 2500)
  } else {
    signalTimer = setTimeout(() => {
      round.value += 1
      beginRound()
    }, 2500)
  }
}

function finishMatch() {
  matchFinished.value = true
  phase.value = 'finished'
  soundManager.stopAll(); soundManager.play(SOUNDS.winner)
  socketStore.send({ event: 'finished', data: { gameId: appStore.game?.id, result: { scores: { ...scores }, rounds: totalRounds, reactions: { ...reactions } } } })
}

function confettiStyle(spark) {
  return { '--x': `${(spark * 37) % 100}%`, '--delay': `${(spark % 12) * -.18}s`, '--drift': `${((spark * 29) % 180) - 90}px`, '--colour': `hsl(${(spark * 43) % 360},90%,62%)` }
}

onMounted(() => {
  eventBus.on('game-control', handleControl)
  eventBus.on('restart-round', resetMatch)
  socketStore.send({ event: 'playing', data: { gameId: game.value.id } })
  resetMatch()
})
onBeforeUnmount(() => {
  clearTimeout(signalTimer)
  soundManager.stopAll()
  eventBus.off('game-control', handleControl)
  eventBus.off('restart-round', resetMatch)
})
</script>

<style scoped>
.go-page{height:100vh;box-sizing:border-box;overflow:hidden;padding:2vh 3vw;background:radial-gradient(circle at 50% 45%,#25324a,#0b101a 65%);color:#fff;text-align:center}.go-page.light{background:radial-gradient(circle at 50% 40%,#fff,#e6ebf3 70%);color:#172033}header p,header h1{margin:0}header p{text-transform:uppercase;letter-spacing:.14em;opacity:.7;font-weight:800}header h1{font-size:clamp(1.7rem,4.5vh,3rem)}.players{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1vh 1vw;margin:1.5vh auto;max-width:1200px}.player{position:relative;min-width:0;display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:.7vw;padding:.7vh .7vw;border:3px solid var(--player-colour,#58b8ff);border-radius:14px;background:#ffffff12;text-align:left}.avatar{width:clamp(1.8rem,5vh,3rem);aspect-ratio:1;display:grid;place-items:center;border-radius:50%;background:var(--player-colour,#58b8ff);color:#111;font-weight:900}.player-info{min-width:0;display:grid;grid-template-rows:auto 1.35em}.player-info strong,.player-status{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.player-status{min-height:1.35em;color:currentColor;opacity:.68;font-size:clamp(.68rem,1.6vh,.9rem);font-weight:500}.player-status.error{color:#ff7a84;opacity:1;font-weight:700}.player-status.submitted{color:#63dc91;opacity:1;font-weight:700}.score{padding-right:.7rem;font-size:clamp(1.5rem,4vh,2.5rem);color:var(--player-colour,#ffd65a)}.connection-dot{position:absolute;right:.35rem;top:.35rem;width:.55rem;aspect-ratio:1;border-radius:50%;background:#40dc74}.connection-dot.away{background:#ffad32}.connection-dot.offline{background:#ff4d5e}.signal{height:44vh;display:grid;place-content:center}.signal-light{--signal-size:min(25vh,20vw);width:var(--signal-size);height:var(--signal-size);min-width:var(--signal-size);min-height:var(--signal-size);box-sizing:border-box;flex:none;display:grid;place-items:center;margin:auto;border-radius:999px;background:#354052;border:7px solid #647086;font-size:clamp(2.5rem,8vh,6rem);font-weight:1000;box-shadow:0 1rem 4rem #0008;transition:.15s}.signal.go .signal-light{background:#42dc7c;border-color:#a6ffc5;color:#092c17;box-shadow:0 0 5rem #42dc7c;transform:scale(1.08)}.signal.round-result .signal-light{background:#ffd65a;border-color:#fff0a0;color:#332800}.signal h2{font-size:clamp(1.2rem,3.5vh,2.5rem);margin:1vh 0 .4vh}.signal p{font-size:clamp(.9rem,2.2vh,1.4rem);margin:0}.result-overlay{position:fixed;inset:0;z-index:50;display:grid;place-items:center;background:#050913df}.result-card{position:relative;z-index:2;width:min(70vw,650px);padding:3vh 4vw;border:3px solid #ffd65a;border-radius:28px;background:#151d2e;color:#fff}.trophy{font-size:clamp(3rem,9vh,6rem)}.result-card h2{font-size:clamp(1.8rem,5vh,3.5rem);margin:0}.result-card ol{list-style:none;max-width:480px;margin:2vh auto 0;padding:0}.result-card li{display:flex;justify-content:space-between;padding:.7vh 1vw;border-bottom:1px solid #ffffff25;font-size:clamp(1rem,2.4vh,1.4rem)}.fireworks{position:absolute;inset:0;overflow:hidden}.fireworks i{position:absolute;left:var(--x);top:-5vh;width:.65rem;height:1rem;background:var(--colour);animation:fall 3s linear infinite;animation-delay:var(--delay)}@keyframes fall{to{transform:translateX(var(--drift)) translateY(115vh) rotate(780deg);opacity:.15}}@media(max-width:750px){.players{grid-template-columns:repeat(2,minmax(0,1fr))}.signal{height:38vh}}
.players{grid-template-columns:repeat(var(--score-columns),minmax(0,1fr))}.result-head,.result-card li{display:grid!important;grid-template-columns:minmax(0,2fr) repeat(3,minmax(70px,1fr));gap:1vw;align-items:center}.result-head{margin-top:2vh;padding:.6vh 1vw;color:#ffd65a;font-size:clamp(.75rem,1.8vh,1rem);font-weight:800}.result-head span:first-child,.result-card li span:first-child{text-align:left}.result-card li b,.result-card li span:not(:first-child){text-align:center}
header p{text-transform:none}
@media(max-width:750px){.players{grid-template-columns:repeat(2,minmax(0,1fr))}}
</style>
